import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { RemoteServiceSetThresholdQueryParams } from '../../../projects/client';

import { RemoteServiceOverlayTypeEnum } from './../../+installations/installation/installation-overlay.model';

import { RemoteServiceActions } from './remote-service.actions';
import {
  RemoteServiceDiagnosisEntry,
  remoteServiceIgnoreNotificationKey,
  remoteServiceNotReadyError,
  RemoteServiceState,
  RemoteServiceThresholdConfiguration,
} from './remote-service.model';

export const initRemoteServiceState: RemoteServiceState = {
  chartsConfig: null,
  chartsFeatures: null,
  startPointMs: null,
  endPointMs: null,
  diagnosisData: [],
  allSummaryData: [],
  annualReportData: {
    deviceSerial: null,
    gatewaySerial: null,
    processId: null,
    fileUri: null,
    reportTemplate: null,
    loading: false,
    loaded: false,
    error: null,
  },
  summaryStatus: {
    loaded: false,
    loading: false,
    dataPresent: false,
  },
  diagnosisStatus: {
    loaded: false,
    loading: false,
    dataPresent: false,
  },
  chartDataStatus: {
    loaded: false,
    loading: false,
    dataPresent: false,
  },
  thresholdsStatus: {
    loaded: false,
    loading: false,
    savingThreshold: null,
  },
  error: null,
  featuresChartsExpanded: true,
  showSideNavEntries: false,
  ignoredRemoteServiceNotifications: null,
  selectedDevice: null,
};

export const remoteServiceReducer: ActionReducer<RemoteServiceState, Action> = createReducer(
  initRemoteServiceState,

  on(
    RemoteServiceActions.loadChartsConfigSuccess,
    (state, { chartsConfig }): RemoteServiceState => ({
      ...state,
      chartsConfig,
    })
  ),

  on(
    RemoteServiceActions.selectDiagnosisEntry,
    (state, { diagnosisEntry }): RemoteServiceState => {
      let diagnosisData = cloneDeep(state.diagnosisData);
      diagnosisData = diagnosisData.map((entry) => ({
        ...entry,
        selected: false,
      }));
      diagnosisData.find((entry) => entry.index === diagnosisEntry.index).selected = true;

      return {
        ...state,
        diagnosisData,
      };
    }
  ),

  on(
    RemoteServiceActions.selectFirstDiagnosisEntry,
    (state): RemoteServiceState => {
      let diagnosisData = cloneDeep(state.diagnosisData);

      diagnosisData = diagnosisData.map((entry) => ({
        ...entry,
        selected: false,
      }));

      if (diagnosisData.length) {
        diagnosisData[0].selected = true;
      }

      return {
        ...state,
        diagnosisData,
      };
    }
  ),

  on(
    RemoteServiceActions.selectFirstAnomalyEntry,
    (state, { overwrite }): RemoteServiceState => {
      const selectedEntryPresent = state.diagnosisData.find((entry) => entry.selected);

      if (selectedEntryPresent && !overwrite) {
        return {
          ...state,
        };
      }

      let diagnosisData = cloneDeep(state.diagnosisData);
      diagnosisData = diagnosisData.map((entry) => ({
        ...entry,
        selected: false,
      }));

      if (diagnosisData.length) {
        const anomalies = diagnosisData.filter((entry) => entry.diagnosis_anomaly === '1');

        if (anomalies.length) {
          anomalies[0].selected = true;
        }
      }

      return {
        ...state,
        diagnosisData,
      };
    }
  ),

  on(
    RemoteServiceActions.loadChartFeatures,
    (state): RemoteServiceState => ({
      ...state,
      chartDataStatus: {
        loaded: false,
        loading: true,
        dataPresent: false,
      },
      error: null,
    })
  ),

  on(
    RemoteServiceActions.loadChartFeaturesSuccess,
    (state, { chartsData }): RemoteServiceState => ({
      ...state,
      chartsFeatures: chartsData,
      chartDataStatus: {
        loaded: true,
        loading: false,
        dataPresent: !!chartsData?.series.length,
      },
    })
  ),

  on(
    RemoteServiceActions.loadChartFeaturesError,
    (state, { error }): RemoteServiceState => ({
      ...state,
      chartsFeatures: null,
      chartDataStatus: {
        loaded: false,
        loading: false,
        dataPresent: false,
      },
      error,
    })
  ),

  on(
    RemoteServiceActions.loadAllSummarySuccess,
    (state, { summary }): RemoteServiceState => ({
      ...state,
      allSummaryData: summary,
    })
  ),

  on(
    RemoteServiceActions.loadDiagnosis,
    (state): RemoteServiceState => ({
      ...state,
      diagnosisData: [],
      diagnosisStatus: {
        loaded: false,
        loading: true,
        dataPresent: false,
      },
      error: null,
    })
  ),

  on(
    RemoteServiceActions.loadDiagnosisSuccess,
    (state, { diagnosisData: overviewData }): RemoteServiceState => {
      const diagnosisData = [...state.diagnosisData];
      diagnosisData.forEach((diagnosisEntry) => {
        const existingElement = overviewData.find(
          (el) => el.diagnosis_id === diagnosisEntry.diagnosis_id
        );
        existingElement.selected = diagnosisEntry.selected;
      });

      return {
        ...state,
        diagnosisData: overviewData,
        diagnosisStatus: {
          isGwWithIdDoesNotExist: false,
          loaded: true,
          loading: false,
          dataPresent: !!overviewData.length,
        },
      };
    }
  ),

  on(
    RemoteServiceActions.loadDiagnosisError,
    (state, { error }): RemoteServiceState => {
      const isGwWithIdDoesNotExist = !!error?.extendedPayload?.details?.includes(
        remoteServiceNotReadyError
      );

      return {
        ...state,
        diagnosisStatus: {
          isGwWithIdDoesNotExist: isGwWithIdDoesNotExist,
          loaded: false,
          loading: false,
          dataPresent: false,
        },
        error,
      };
    }
  ),

  on(
    RemoteServiceActions.setChartExtremes,
    (state, { endPointMs, startPointMs }): RemoteServiceState => ({
      ...state,
      startPointMs,
      endPointMs,
    })
  ),

  on(
    RemoteServiceActions.saveThreshold,
    (state, { params }): RemoteServiceState => {
      return {
        ...state,
        thresholdsStatus: {
          loaded: false,
          loading: true,
          savingThreshold: params.name,
        },
        error: null,
      };
    }
  ),

  on(
    RemoteServiceActions.saveThresholdSuccess,
    (state, { params }): RemoteServiceState => {
      const updatedDiagnosisData = cloneDeep(state.diagnosisData);
      const configToUpdate = findThresholdConfiguration(updatedDiagnosisData, params);
      configToUpdate.value = params.value;

      return {
        ...state,
        diagnosisData: updatedDiagnosisData,
        thresholdsStatus: {
          loaded: true,
          loading: false,
          savingThreshold: null,
        },
      };
    }
  ),

  on(
    RemoteServiceActions.saveThresholdError,
    (state, { error }): RemoteServiceState => {
      return {
        ...state,
        thresholdsStatus: {
          loaded: false,
          loading: false,
          savingThreshold: null,
        },
        error,
      };
    }
  ),

  on(
    RemoteServiceActions.clearDetailedInspectionState,
    (state): RemoteServiceState => ({
      ...initRemoteServiceState,
      summaryStatus: state.summaryStatus,
      allSummaryData: state.allSummaryData,
    })
  ),

  on(
    RemoteServiceActions.clearState,
    (): RemoteServiceState => ({
      ...initRemoteServiceState,
    })
  ),

  on(
    RemoteServiceActions.createAnnualReport,
    (state, { reportTemplate, deviceSerial, gatewaySerial }): RemoteServiceState => ({
      ...state,
      annualReportData: {
        deviceSerial,
        gatewaySerial,
        processId: null,
        fileUri: null,
        reportTemplate,
        loaded: false,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    RemoteServiceActions.createAnnualReportSuccess,
    (state, { processId }): RemoteServiceState => ({
      ...state,
      annualReportData: {
        deviceSerial: state.annualReportData.deviceSerial,
        gatewaySerial: state.annualReportData.gatewaySerial,
        processId,
        fileUri: null,
        reportTemplate: state.annualReportData.reportTemplate,
        loaded: false,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    RemoteServiceActions.createAnnualReportError,
    (state, { error }): RemoteServiceState => ({
      ...state,
      annualReportData: {
        deviceSerial: state.annualReportData.deviceSerial,
        gatewaySerial: state.annualReportData.gatewaySerial,
        processId: null,
        fileUri: null,
        reportTemplate: state.annualReportData.reportTemplate,
        loaded: false,
        loading: false,
        error,
      },
    })
  ),

  on(
    RemoteServiceActions.getAnnualReportUrl,
    (state): RemoteServiceState => ({
      ...state,
      annualReportData: {
        deviceSerial: state.annualReportData.deviceSerial,
        gatewaySerial: state.annualReportData.gatewaySerial,
        processId: state.annualReportData.processId,
        fileUri: null,
        reportTemplate: state.annualReportData.reportTemplate,
        loaded: false,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    RemoteServiceActions.getAnnualReportUrlSuccess,
    (state, { fileUri }): RemoteServiceState => ({
      ...state,
      annualReportData: {
        deviceSerial: state.annualReportData.deviceSerial,
        gatewaySerial: state.annualReportData.gatewaySerial,
        processId: state.annualReportData.processId,
        fileUri,
        reportTemplate: state.annualReportData.reportTemplate,
        loaded: true,
        loading: false,
        error: null,
      },
    })
  ),

  on(
    RemoteServiceActions.getAnnualReportUrlError,
    (state, { error }): RemoteServiceState => ({
      ...state,
      annualReportData: {
        deviceSerial: state.annualReportData.deviceSerial,
        gatewaySerial: state.annualReportData.gatewaySerial,
        processId: state.annualReportData.processId,
        fileUri: null,
        reportTemplate: state.annualReportData.reportTemplate,
        loaded: false,
        loading: false,
        error,
      },
    })
  ),

  on(
    RemoteServiceActions.tabChanged,
    (state, { tabType }): RemoteServiceState => {
      let diagnosisData = cloneDeep(state.diagnosisData);
      diagnosisData = diagnosisData.map((entry) => ({
        ...entry,
        selected: false,
      }));

      if (tabType === RemoteServiceOverlayTypeEnum.Anomalies) {
        const firstAnomaly = diagnosisData.find((entry) => entry.diagnosis_anomaly === '1');

        if (firstAnomaly) {
          firstAnomaly.selected = true;
        }
      } else if (tabType === RemoteServiceOverlayTypeEnum.Diagnosis) {
        const firstDiagnosis = diagnosisData[0];

        if (firstDiagnosis) {
          firstDiagnosis.selected = true;
        }
      }

      return {
        ...state,
        diagnosisData,
      };
    }
  ),

  on(
    RemoteServiceActions.getLowBudgetNotification,
    (state): RemoteServiceState => {
      const rawIgnoredRemoteServiceNotifications = localStorage.getItem(
        remoteServiceIgnoreNotificationKey
      );

      const ignoredRemoteServiceNotifications = rawIgnoredRemoteServiceNotifications
        ? JSON.parse(rawIgnoredRemoteServiceNotifications)
        : [];

      return { ...state, ignoredRemoteServiceNotifications };
    }
  ),

  on(
    RemoteServiceActions.selectDevice,
    (state, { device }): RemoteServiceState => ({ ...state, selectedDevice: device })
  )
);

function findThresholdConfiguration(
  diagnosisData: RemoteServiceDiagnosisEntry[],
  params: RemoteServiceSetThresholdQueryParams
): RemoteServiceThresholdConfiguration {
  let thresholdConfiguration;

  diagnosisData.forEach((entry) => {
    const config = entry.diagnosis_configurations.find((configuration) => {
      return configuration.name === params.name && configuration.group === params.group;
    });

    if (config) {
      thresholdConfiguration = config;
    }
  });

  return thresholdConfiguration;
}
